import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { NOTIFICATIONS } from 'src/app/shared/constants';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import {
    NotificationSettingsResponse,
    TwoFactorAuthSecret,
    TwoFactorAuthSettings,
} from '../../models/api/responses/profileSettings.response';
import { SocialAccount } from '../../models/profile.interface';
import { ProfileSettingsService } from '../../services/profile-settings.service';
import { ProfileSettingsActions } from '../actions/actions-type.action';

@Injectable()
export class ProfileSettingsEffect {
    private actions$ = inject(Actions);
    private profileSettingsService = inject(ProfileSettingsService);
    private notificationsService = inject(NotificationsService);
    fetchTwoFactorAuthSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.fetchTwoFactorAuthSettings),
            switchMap((action) =>
                this.profileSettingsService.fetchTwoFactorAuthSettings().pipe(
                    map((settings: TwoFactorAuthSettings) =>
                        ProfileSettingsActions.fetchTwoFactorAuthSettingsSuccess(
                            {
                                payload: settings,
                            }
                        )
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            ProfileSettingsActions.fetchTwoFactorAuthSettingsFailure(
                                {
                                    error: response,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    enableTwoFactorAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.enableTwoFactorAuth),
            switchMap((action) =>
                this.profileSettingsService
                    .enableTwoFactorAuth(action.payload)
                    .pipe(
                        map((secret: TwoFactorAuthSecret) =>
                            ProfileSettingsActions.enableTwoFactorAuthSuccess({
                                payload: secret,
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileSettingsActions.enableTwoFactorAuthFailure(
                                    {
                                        error: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    disableTwoFactorAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.disableTwoFactorAuth),
            switchMap((action) =>
                this.profileSettingsService
                    .disableTwoFactorAuth(action.payload)
                    .pipe(
                        map(() => {
                            this.notificationsService.openSnackBar({
                                title: 'Two-factor authentication has been disabled.',
                                type: SnackBarType.Success,
                            });
                            return ProfileSettingsActions.disableTwoFactorAuthSuccess();
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileSettingsActions.disableTwoFactorAuthFailure(
                                    {
                                        error: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    codeVerifyTwoFactorAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.codeVerificationTwoFactorAuth),
            switchMap((action) =>
                this.profileSettingsService
                    .verifyTwoFactorAuth(action.payload)
                    .pipe(
                        map(() => {
                            this.notificationsService.openSnackBar({
                                title: 'Two-factor authentication has been enabled.',
                                type: SnackBarType.Success,
                            });

                            return ProfileSettingsActions.codeVerificationTwoFactorAuthSuccess();
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileSettingsActions.codeVerificationTwoFactorAuthFailure(
                                    {
                                        error: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveNotificationSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.saveNotificationSettings),
            mergeMap((action) =>
                this.profileSettingsService
                    .saveNotificationSettings(action.payload)
                    .pipe(
                        map(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.PROFILE_INFORMATION_UPDATED,
                                type: SnackBarType.Success,
                            });

                            return ProfileSettingsActions.saveNotificationSettingsSuccess(
                                {
                                    payload:
                                        action.payload as NotificationSettingsResponse,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileSettingsActions.saveNotificationSettingsFailure(
                                    {
                                        error: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    getUserNotificationSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.getUserNotificationSettings),
            switchMap((action) =>
                this.profileSettingsService
                    .getUserNotificationSettings(action.payload.playerId)
                    .pipe(
                        map((response: NotificationSettingsResponse) =>
                            ProfileSettingsActions.getUserNotificationSettingsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileSettingsActions.getUserNotificationSettingsFailure(
                                    {
                                        error: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveUserSocialAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.saveUserSocialAccounts),
            exhaustMap((action) =>
                this.profileSettingsService
                    .saveUserSocialAccounts(action.payload)
                    .pipe(
                        map(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.PROFILE_INFORMATION_UPDATED,
                                type: SnackBarType.Success,
                            });

                            return ProfileSettingsActions.saveUserSocialAccountsSuccess(
                                {
                                    payload: action.payload.accounts,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileSettingsActions.saveUserSocialAccountsFailure(
                                    {
                                        error: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchSavedUserSocialAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.fetchSavedUserSocialAccounts),
            switchMap((action) =>
                this.profileSettingsService
                    .fetchSavedUserSocialAccounts(action.payload.playerId)
                    .pipe(
                        map((response: SocialAccount[]) =>
                            ProfileSettingsActions.fetchSavedUserSocialAccountsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileSettingsActions.fetchSavedUserSocialAccountsFailure(
                                    {
                                        error: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchSupportedSocialAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileSettingsActions.fetchSupportedSocialAccounts),
            switchMap(() =>
                this.profileSettingsService.fetchSupportedSocialAccounts().pipe(
                    map((response: SocialAccount[]) =>
                        ProfileSettingsActions.fetchSupportedSocialAccountsSuccess(
                            {
                                payload: response,
                            }
                        )
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            ProfileSettingsActions.fetchSupportedSocialAccountsFailure(
                                {
                                    error: response,
                                }
                            )
                        )
                    )
                )
            )
        )
    );
}
